import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import ParticlesAuth from "components/common/ParticlesAuth";
import { getDocumentTitle } from "utils/helpers";
import resetPasswordSchema from "./schemas/resetPassword.schema";
import logoSm from "assets/images/logo-sm.png";
import { resetPassword } from "./services/AuthService";

function messageFromResetError(err: unknown): string {
  const ax = err as {
    response?: {
      data?: {
        message?: string;
        errors?: Record<string, string[] | string> | unknown[];
      };
    };
  };
  const d = ax.response?.data;
  if (!d) return "Could not reset password";
  if (typeof d.message === "string" && d.message.trim()) return d.message;
  const errors = d.errors;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    const first = Object.values(errors as Record<string, unknown>)[0];
    if (Array.isArray(first) && first[0] != null) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return "Could not reset password";
}

function tokenFromUrl(
  tokenParam: string | undefined,
  searchParams: URLSearchParams,
): string {
  const fromPath = tokenParam?.trim() ?? "";
  if (fromPath) {
    try {
      return decodeURIComponent(fromPath).trim();
    } catch {
      return fromPath;
    }
  }
  return searchParams.get("token")?.trim() ?? "";
}

const ResetPassword = () => {
  const { token: tokenFromPath } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [passwordShow, setPasswordShow] = useState(false);

  const token = useMemo(
    () => tokenFromUrl(tokenFromPath, searchParams),
    [tokenFromPath, searchParams],
  );

  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!token) return;
      setSubmitting(true);
      try {
        const res = await resetPassword({
          token,
          newPassword: values.newPassword,
        });
        if (res.success) {
          toast.success(
            (typeof res.message === "string" && res.message.trim()) ||
              "Password updated. You can sign in now.",
          );
          navigate("/login", { replace: true });
        } else {
          toast.error(res.message || "Could not reset password");
        }
      } catch (err: unknown) {
        toast.error(messageFromResetError(err));
      } finally {
        setSubmitting(false);
      }
    },
  });

  document.title = getDocumentTitle("Reset password");

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center min-vh-100 align-items-center py-5">
              <Col xs={12}>
                <div className="text-center mb-4">
                  <div className="mb-2">
                    <img src={logoSm} alt="Automation Engineers" height="52" />
                  </div>
                  <h4 className="fw-bold text-dark mb-1">AUTOMATION ENGINEERS</h4>
                  <p className="text-muted mb-0">Set a new password for your account</p>
                </div>
              </Col>

              <Col md={8} lg={6} xl={5}>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Set a new password</h5>
                      <p className="text-muted">
                        Choose a new password for your account.
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      {!token ? (
                        <Alert color="warning" className="mb-0">
                          This page needs a reset token in the URL (for example{" "}
                          <code className="small">/reset-password?token=…</code>{" "}
                          or <code className="small">/reset-password/…</code>
                          ). Request a new link from{" "}
                          <Link to="/forgot-password">Forgot password</Link>.
                        </Alert>
                      ) : (
                        <Form onSubmit={formik.handleSubmit}>
                          <div className="mb-3">
                            <Label htmlFor="rp-new" className="form-label">
                              New password
                            </Label>
                            <div className="position-relative auth-pass-inputgroup mb-1">
                              <Input
                                id="rp-new"
                                name="newPassword"
                                type={passwordShow ? "text" : "password"}
                                autoComplete="new-password"
                                className="form-control pe-5"
                                value={formik.values.newPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  !!(
                                    formik.touched.newPassword &&
                                    formik.errors.newPassword
                                  )
                                }
                              />
                              {formik.touched.newPassword &&
                              formik.errors.newPassword ? (
                                <FormFeedback type="invalid">
                                  {formik.errors.newPassword}
                                </FormFeedback>
                              ) : null}
                              <button
                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                type="button"
                                aria-label={
                                  passwordShow
                                    ? "Hide password"
                                    : "Show password"
                                }
                                onClick={() => setPasswordShow(!passwordShow)}
                              >
                                <i className="ri-eye-fill align-middle" />
                              </button>
                            </div>
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="rp-confirm" className="form-label">
                              Confirm new password
                            </Label>
                            <Input
                              id="rp-confirm"
                              name="confirmPassword"
                              type={passwordShow ? "text" : "password"}
                              autoComplete="new-password"
                              value={formik.values.confirmPassword}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                !!(
                                  formik.touched.confirmPassword &&
                                  formik.errors.confirmPassword
                                )
                              }
                            />
                            {formik.touched.confirmPassword &&
                            formik.errors.confirmPassword ? (
                              <FormFeedback type="invalid">
                                {formik.errors.confirmPassword}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <div className="mt-4">
                            <Button
                              color="primary"
                              className="w-100"
                              type="submit"
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Saving…
                                </>
                              ) : (
                                "Update password"
                              )}
                            </Button>
                          </div>
                        </Form>
                      )}
                      <div className="mt-4 text-center">
                        <Link to="/login" className="text-muted">
                          <i className="mdi mdi-chevron-left" /> Back to sign in
                        </Link>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ResetPassword;
